import config from 'player/config';

import { TOKEN_FETCH_ERROR } from '../playback/config/status-codes';
import { StreamModel } from './stream';

class Token {
    isFetching: boolean = false;

    data: {
        /**
         * Akamai token value retrieved from SVP api
         */
        value: string | null;
        /**
         * Expiration time, unix timestamp
         */
        expiry: number;
    };

    request: Promise<null | string> | null = null;

    constructor() {
        this.data = { value: null, expiry: 0 };
    }

    private async fetchToken(url: string) {
        this.isFetching = true;

        const response = await fetch(url);

        this.isFetching = false;

        if (!response.ok) {
            return Promise.reject(TOKEN_FETCH_ERROR);
        }

        return response.text();
    }

    public async fetch(
        stream: StreamModel,
        configuration?: ConfigFn,
        maxBitrate?: number,
    ) {
        const vendor = stream.getVendor();
        if (!this.isValid()) {
            if (this.request && this.isFetching) {
                return this.request;
            }

            if (typeof configuration === 'function') {
                const assetId = stream.getId();
                const { value, expiry } =
                    (await configuration(assetId, stream.get('access')!)) || {};

                if (value && expiry) {
                    const url = this.url(
                        { vendor, expiry, value, assetId },
                        maxBitrate,
                    );
                    this.request = this.fetchToken(url);
                    this.request.then((v) => {
                        if (v) {
                            this.data = { value: v, expiry: Date.now() + 1000 };
                        }
                    });
                    return this.request;
                }
            }

            if (!stream.hasAccess()) {
                const url = this.url({ vendor }, maxBitrate);
                this.request = this.fetchToken(url);
                this.request.then((v) => {
                    if (v) {
                        this.data = { value: v, expiry: Date.now() + 1000 };
                    }
                });
                return this.request;
            }

            this.request = Promise.resolve(null);
        }

        return this.request;
    }

    private url(params: TokenParams, maxBitrate?: number) {
        const query = [`vendor=${params.vendor}`];

        if (params.expiry && params.value && params.assetId) {
            query.push(`assetId=${params.assetId}`);
            query.push(`expires=${params.expiry}`);
            query.push(`hmac=${params.value}`);
        } else {
            query.push('acl=/*');
        }

        if (maxBitrate) {
            query.push(`maxBitrate=${maxBitrate}`);
        }

        return `${config.api.tokenUrl}?${query.join('&')}`;
    }

    private getValue() {
        return this.data.value;
    }

    async getSignedUrl(
        stream: StreamModel,
        configuration: ConfigFn,
        maxBitrate?: number,
    ) {
        await this.fetch(stream, configuration, maxBitrate);
        const value = this.getValue();
        if (!this.isValid() || !value) {
            return null;
        }

        const hlsUrl = stream.getUrl('hls');
        return `${hlsUrl}?hdnea=${encodeURIComponent(value)}`;
    }

    public isValid() {
        // on load expiry is 0 therefore it will always return false
        return Date.now() < this.data.expiry;
    }

    public async getDrmToken() {
        return null;
    }

    public isDrmTokenValid() {
        return false;
    }
}

export type TokenParams = {
    vendor: string;
    expiry?: number;
    value?: string;
    assetId?: number;
};

export type ConfigFn = (
    assetId: number,
    assetAccess?: string,
) => Promise<{ expiry: number; value: string } | null>;

export default Token;
